<script setup lang="ts">
import { ref } from "vue";
import AssigneeField from "./AssigneeField.vue";
import { useUserStore } from "@/stores/user";
import { toast } from "vue-sonner";
import useTrackerStore from "@/stores/trackers";
import { RUDDERSTACK_EVENTS } from "@/lib/rudderstack";
import { Button } from "@/components/ui/button";
import { useUpdateTicket } from "@/api/mutations/customerSupport";
import { TicketData } from "@telegraphio/bapi-types";
import { buildUpdateTicketRequest } from "@/utils/customerSupportHelpers";

type Props = {
  ticket: TicketData;
};

const props = defineProps<Props>();
const trackers = useTrackerStore();

const emit = defineEmits<{
  assigned: [];
}>();

const userStore = useUserStore();

const updateTicketMutation = useUpdateTicket();

const trigger = ref<HTMLButtonElement | null>(null);
const popover = ref<HTMLDivElement | null>(null);
function onToggle(e: ToggleEvent) {
  const body = document.querySelector("body");
  if (body) {
    if (e.newState === "open") {
      body.style.overflow = "hidden";
      if (trigger.value && popover.value) {
        const { right: triggerRight, top: triggerTop } = trigger.value.getBoundingClientRect();
        const { width: popoverWidth, height: popoverHeight } = popover.value.getBoundingClientRect();
        popover.value.style.top = `${triggerTop - popoverHeight - 4}px`;
        popover.value.style.left = `${triggerRight - popoverWidth}px`;
      }
      return;
    }
    body.style.overflow = "auto";
  }
}

async function assignTicket(userId?: string) {
  const assignee = userId ?? userStore.user?.user_id;
  if (!assignee) return undefined;
  const rudderStackEvent =
    assignee === userStore.user?.user_id
      ? RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_ASSIGN_SELF
      : RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_ASSIGN_OTHER;

  const requestData = buildUpdateTicketRequest(props.ticket, {
    assignees: [assignee],
    state: "In Progress",
  });

  const result = await updateTicketMutation
    .mutateAsync({
      customerId: props.ticket.customer_id,
      ticketId: props.ticket.id.toString(),
      requestData,
    })
    .catch(() => {
      toast.error("Unable to reassign ticket.");
      trackers.logRudderstackEvent(rudderStackEvent, { success: false, assignee });
      return undefined;
    });

  if (!result) return;

  toast.success("Ticket assigned!");
  popover.value?.hidePopover();
  trackers.logRudderstackEvent(rudderStackEvent, { success: true, assignee });
  emit("assigned");
}
</script>

<template>
  <div class="relative">
    <Button
      ref="trigger"
      :popovertarget="`${ticket.id}-details-assign`"
      variant="outline"
      @click="trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_START_ASSIGN, {})"
    >
      {{ ticket.assignees?.length ? "Re-assign Ticket" : "Assign Ticket" }}
    </Button>
    <div :id="`${ticket.id}-details-assign`" ref="popover" class="absolute inset-[unset]" popover @toggle="onToggle">
      <AssigneeField :customer-id="ticket.customer_id" @assign="assignTicket" @self-assign="assignTicket" />
    </div>
  </div>
</template>
